import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, Input, useToast } from '@caruso/ui';
import { processesService, ProcessFrequency } from '@/services/processes.service';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Clock,
  Camera,
  CheckCircle,
  XCircle,
  ListTodo,
} from 'lucide-react';

const frequencyLabels: Record<ProcessFrequency, string> = {
  [ProcessFrequency.DAILY]: 'Diário',
  [ProcessFrequency.WEEKLY]: 'Semanal',
  [ProcessFrequency.MONTHLY]: 'Mensal',
};

export function ProcessesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: processes, isLoading } = useQuery({
    queryKey: ['processes'],
    queryFn: () => processesService.getAll(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => processesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['processes'] });
      toast({
        title: 'Processo removido',
        description: 'O processo foi removido com sucesso',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao remover processo',
        variant: 'destructive',
      });
    },
  });

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Tem certeza que deseja excluir o processo "${name}"?`)) {
      return;
    }
    deleteMutation.mutate(id);
  };

  const filteredProcesses = processes?.filter((process) =>
    process.name.toLowerCase().includes(search.toLowerCase()) ||
    process.description?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Processos</h1>
          <p className="text-brand-ivory/60 mt-1">
            Gerencie os processos a serem executados pelos funcionários
          </p>
        </div>
        <Button onClick={() => navigate('/app/processos/novo')} variant="premium">
          <Plus className="w-4 h-4 mr-2" />
          Novo Processo
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold/50" />
            <Input
              placeholder="Buscar por nome ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-brand-smoke/50 border-brand-gold/20 text-brand-ivory"
            />
          </div>
        </div>
      </Card>

      {/* Processes List */}
      {isLoading ? (
        <div className="text-center py-12 text-brand-ivory/50">
          Carregando...
        </div>
      ) : (
        <>
          <div className="grid gap-4">
            {filteredProcesses?.map((process) => (
              <motion.div
                key={process.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ scale: 1.01 }}
              >
                <Card className="bg-brand-ink/50 border-brand-gold/20 hover:border-brand-gold/40 transition-all">
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/app/processos/editar/${process.id}`)}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <ListTodo className="w-5 h-5 text-brand-gold" />
                          <h3 className="text-lg font-serif text-brand-gold">
                            {process.name}
                          </h3>
                        </div>
                        {process.description && (
                          <p className="text-sm text-brand-ivory/60 mb-3">
                            {process.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                            {frequencyLabels[process.frequency]}
                          </span>
                          {process.active ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                              <CheckCircle className="h-3 w-3" />
                              Ativo
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/30">
                              <XCircle className="h-3 w-3" />
                              Inativo
                            </span>
                          )}
                          <span className="text-xs text-brand-ivory/50">
                            {process.tasks.length} {process.tasks.length === 1 ? 'tarefa' : 'tarefas'}
                          </span>
                          {process._count && (
                            <span className="text-xs text-brand-ivory/50">
                              {process._count.executions} {process._count.executions === 1 ? 'execução' : 'execuções'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/app/processos/editar/${process.id}`)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(process.id, process.name)}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>

                    {/* Tasks Preview */}
                    {process.tasks.length > 0 && (
                      <div className="pt-4 border-t border-brand-gold/20">
                        <h4 className="text-sm font-semibold text-brand-gold mb-3">Tarefas:</h4>
                        <div className="space-y-2">
                          {process.tasks.slice(0, 3).map((task, index) => (
                            <div
                              key={task.id}
                              className="flex items-start gap-3 text-sm p-3 bg-brand-smoke/30 rounded border border-brand-gold/10"
                            >
                              <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-brand-gold/20 text-brand-gold text-xs font-bold">
                                {index + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-brand-ivory truncate">{task.name}</p>
                                {task.description && (
                                  <p className="text-xs text-brand-ivory/50 mt-1 line-clamp-1">
                                    {task.description}
                                  </p>
                                )}
                                <div className="flex items-center gap-3 text-xs text-brand-ivory/50 mt-2">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    <span>
                                      {task.minTime} - {task.maxTime}
                                    </span>
                                  </div>
                                  {task.photoRequired && (
                                    <div className="flex items-center gap-1 text-blue-400">
                                      <Camera className="h-3 w-3" />
                                      <span>Foto obrigatória</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {process.tasks.length > 3 && (
                            <p className="text-xs text-brand-ivory/50 text-center py-2">
                              +{process.tasks.length - 3} {process.tasks.length - 3 === 1 ? 'tarefa' : 'tarefas'}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {filteredProcesses?.length === 0 && (
            <div className="text-center py-12">
              <ListTodo className="w-16 h-16 mx-auto text-brand-gold/20 mb-4" />
              <p className="text-brand-ivory/50">
                {search
                  ? 'Nenhum processo encontrado'
                  : 'Nenhum processo cadastrado ainda'}
              </p>
              {!search && (
                <Button
                  onClick={() => navigate('/app/processos/novo')}
                  variant="outline"
                  className="mt-4"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar primeiro processo
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
